export interface Category {
  name: string;
  description?: string | null;
  displayOrder?: number | null;
  parentCategoryName?: string | null;
}

export const MAX_CATEGORY_DEPTH = 3;

export interface CategoryTreeNode extends Category {
  children: CategoryTreeNode[];
  depth: number;
}

export function buildCategoryForest(
  categories: Category[],
): CategoryTreeNode[] {
  const byName = new Map<string, CategoryTreeNode>();
  for (const c of categories) {
    byName.set(c.name, { ...c, children: [], depth: 0 });
  }
  const roots: CategoryTreeNode[] = [];
  // First pass: link children to parents. Depth is NOT set here because it would
  // depend on iteration order — a grandchild appearing in the input before its
  // parent would read a stale `parent.depth` of 0.
  // Array.from for downlevelIteration compatibility (prod tsconfig target).
  for (const node of Array.from(byName.values())) {
    const parentName = node.parentCategoryName ?? null;
    if (parentName && byName.has(parentName)) {
      const parent = byName.get(parentName)!;
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }
  // Second pass: walk from each root and stamp depth. Order-independent.
  const setDepth = (node: CategoryTreeNode, depth: number) => {
    node.depth = depth;
    for (const child of node.children) setDepth(child, depth + 1);
  };
  for (const root of roots) setDepth(root, 0);
  return roots;
}

export function expandCategoryTree(
  categories: Category[],
  selectedNames: Iterable<string>,
): Set<string> {
  const childrenByParent = new Map<string, string[]>();
  for (const c of categories) {
    const parent = c.parentCategoryName ?? null;
    if (!parent) continue;
    const list = childrenByParent.get(parent) ?? [];
    list.push(c.name);
    childrenByParent.set(parent, list);
  }
  const expanded = new Set<string>();
  // Array.from for downlevelIteration compatibility (the prod tsconfig target
  // doesn't allow spread of non-array iterables).
  const stack = Array.from(selectedNames);
  while (stack.length) {
    const name = stack.pop()!;
    if (expanded.has(name)) continue;
    expanded.add(name);
    const children = childrenByParent.get(name);
    if (children) stack.push(...children);
  }
  return expanded;
}

export interface CategoryUsageItem {
  id: string;
  name: string;
}

export interface CategoryUsageDetail {
  items: CategoryUsageItem[];
  commissions: CategoryUsageItem[];
  memberships: CategoryUsageItem[];
}

export interface CategoryTable extends Category {
  usage: CategoryUsageDetail;
  /** The enterprise's record this one follows, if any. */
  enterpriseRef?: string | null;
}
